import { Alert, LoadingOverlay, Stack, Text } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { TbAlertTriangle } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'

import { getAffectedRoutesOptions } from '@/api/main/@tanstack/react-query.gen'

import CopyableId from './CopyableId'

type Props = {
  segmentIds: Array<string>
}

const LINK_STYLE = {
  color: 'var(--mantine-color-blue-6)',
  textDecoration: 'underline',
  fontFamily: 'var(--mantine-font-family-monospace)',
}

const AffectedRoutesPanel = ({ segmentIds }: Props) => {
  const { t } = useTranslation('unexpectedEvents')

  const enabled = segmentIds.length > 0
  const query = useQuery({
    ...getAffectedRoutesOptions({ body: { segments: segmentIds } }),
    enabled,
  })

  return (
    <Stack gap="xs" pos="relative" mih={48}>
      <LoadingOverlay visible={query.isLoading && enabled} />

      {!enabled && (
        <Text size="sm" c="dimmed">
          {t('panels.noAffectedSegments')}
        </Text>
      )}

      {query.isError && (
        <Alert color="red" icon={<TbAlertTriangle />}>
          {t('panels.affectedRoutesError')}
        </Alert>
      )}

      {enabled && query.data && query.data.routes.length === 0 && (
        <Text size="sm" c="dimmed">
          {t('panels.noAffectedRoutes')}
        </Text>
      )}

      {query.data?.routes.map(route => {
        const display = `${route.route_id} (${route.route_version})`
        return (
          <CopyableId
            key={`${route.route_id}-${route.route_version}`}
            value={route.route_id}
            display={
              <Link
                to="/route/$routeId/$version"
                params={{ routeId: String(route.route_id), version: String(route.route_version) }}
                style={LINK_STYLE}
                title={display}
              >
                {display}
              </Link>
            }
          />
        )
      })}
    </Stack>
  )
}

export default AffectedRoutesPanel
